import React from 'react';
import {graphql, readInlineData} from 'react-relay';
import {Redirect} from 'react-router-dom';
import {reportBuilderPathFor} from './reportBuilderRedirect';
import {REPORT_TYPE} from '../../../constants';
import ReportedTimePage, {
	reportedTimePageQuery,
} from '../../../components/insights/reported-time-portfolio-report/reported_time_page';
import TaskReportPageV2, {taskReportPageV2Query} from '../report/task-report-page/TaskReportPageV2';

import {GenericReportContextProvider} from './GenericReportContext';
import UtilizationReport, {UtilizationReportQuery} from '../../../components/insights/utilization-report/UtilizationReport';
import ProjectPortfolioReport, {
	ProjectPortfolioReportQuery,
} from '../../../components/insights/project-portfolio-report/ProjectPortfolioReport';
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import TaskReportPage, {taskReportPageQuery} from '../../../components/insights/task-report/TaskReportPage';
import TimeRegistrationReportPage, {
	TimeRegistrationReportPageQuery,
} from '../report/time-registration-report-page/TimeRegistrationReportPage';

const GenericSavedReportConductor = ({actualPersonId, savedReport, sharedId, shareType}) => {
	const componentWrapper = (key, name, query, type, ReportComponent, authorizeAccessRoute) => {
		return (
			<ForecastQueryRenderer
				key={key}
				query={query}
				variables={{savedReportId: savedReport.id, sharedId: sharedId}}
				renderIfNotLoggedIn={!!sharedId}
				render={relayProps => {
					return (
						<GenericReportContextProvider
							actualPersonId={actualPersonId}
							savedReportRef={savedReport}
							reportType={type}
							authorizeAccessRoute={authorizeAccessRoute}
							sharedId={sharedId}
							shareType={shareType}
						>
							<ReportComponent {...relayProps} />
						</GenericReportContextProvider>
					);
				}}
			/>
		);
	};

	// A report authored in the AI report builder opens there, whichever route brought it here: this
	// conductor also sits behind share links and direct saved-report URLs, which never consult the
	// origin the Reports list checks. Read through the inline fragment because the callers spread it
	// rather than selecting the origin themselves.
	const builderPath = reportBuilderPathFor(processSavedReport(savedReport), shareType);
	if (builderPath !== null) {
		return <Redirect to={builderPath} />;
	}

	const newReportService = savedReport.reportService || sharedId;

	switch (savedReport.type) {
		case REPORT_TYPE.TIME_REG: {
			return componentWrapper(
				'query-render-reportedTimePage',
				'reported_time_page',
				newReportService ? TimeRegistrationReportPageQuery : reportedTimePageQuery,
				savedReport.type,
				newReportService ? TimeRegistrationReportPage : ReportedTimePage
			);
		}
		case REPORT_TYPE.PROJECT_PORTFOLIO_REPORT: {
			return componentWrapper(
				'query-render-projectPortfolioReportQuery',
				'project_portfolio_report',
				ProjectPortfolioReportQuery,
				savedReport.type,
				ProjectPortfolioReport,
				'project-portfolio-report'
			);
		}
		case REPORT_TYPE.UTILIZATION_REPORT: {
			return componentWrapper(
				'query-render-reportedTimePage',
				'utilization_report',
				UtilizationReportQuery,
				savedReport.type,
				UtilizationReport,
				'utilization-report'
			);
		}
		case REPORT_TYPE.TASK_REPORT: {
			return componentWrapper(
				'query-render-task-report',
				'task_report_page',
				newReportService ? taskReportPageV2Query : taskReportPageQuery,
				savedReport.type,
				newReportService ? TaskReportPageV2 : TaskReportPage,
				'task-report'
			);
		}

		default: {
			// A report type this conductor has no page for -- today, a Project Status report reached
			// through the standard saved-report route or an external share link, neither of which
			// gates on the report builder.
			//
			// Returning nothing is NOT free: React 16 throws "Nothing was returned from render" on
			// undefined, ForecastQueryRenderer's ErrorBoundary catches it, and the reader gets a
			// full-page error plus a bugduty Sentry event -- for a report that merely has no page
			// here yet. An explicit null renders nothing instead, which is the honest outcome for a
			// type this switch does not handle.
			//
			// This is containment, not the fix: a Project Status report opened from the Reports list
			// should reach the report builder. That belongs with the ticket that owns listing and
			// reopening.
			return null;
		}
	}
};

export default GenericSavedReportConductor;

export function processSavedReport(savedReportRef) {
	const savedReport = readInlineData(
		graphql`
			fragment GenericSavedReportConductor_savedReport on SavedReportType @inline {
				id
				updatedAt
				updatedBy {
					id
					fullName
				}
				type
				name
				source
				person {
					id
					active
					email
					fullName
					profilePictureId
				}
				groupingOne
				groupingTwo
				groupingThree
				groupingFour
				eyeApplied
				filterApplied
				filters {
					field
					value
					operator
				}
				startDate
				endDate
				periodType
				periodDate
				reportService
				shares(first: 100) @connection(key: "Report_shares", filters: []) {
					edges {
						node {
							id
							user {
								id
								email
							}
						}
					}
				}
				privateAccess
				state
				layout {
					field
					width
				}
				sort {
					field
					direction
				}
			}
		`,
		savedReportRef
	);
	return savedReport;
}
